import React, { ChangeEvent } from 'react';

// ── Inline preview formatter (mirrors backend formatTemporal.ts) ────

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const EXAMPLE_VALUE = {
    year: 2024,
    month: 3,
    day: 15,
    hour: 14,
    minute: 30,
    second: 45,
    millisecond: 123,
    timeZone: 'America/New_York',
    offset: '-04:00',
    // Pre-computed: 2024-03-15 is a Friday (dayOfWeek = 5)
    _dayOfWeek: 5,
};

function pad(value: number, length: number): string {
    return String(value).padStart(length, '0');
}

function hour24to12(hour: number): number {
    const result = hour % 12;
    return result === 0 ? 12 : result;
}

const PREVIEW_TOKENS: Array<{ token: string; resolve: () => string }> = [
    { token: 'yyyy', resolve: () => pad(EXAMPLE_VALUE.year, 4) },
    { token: 'yy', resolve: () => pad(EXAMPLE_VALUE.year % 100, 2) },
    { token: 'MMMM', resolve: () => MONTH_NAMES[EXAMPLE_VALUE.month - 1] },
    { token: 'MMM', resolve: () => MONTH_NAMES_SHORT[EXAMPLE_VALUE.month - 1] },
    { token: 'MM', resolve: () => pad(EXAMPLE_VALUE.month, 2) },
    { token: 'M', resolve: () => String(EXAMPLE_VALUE.month) },
    { token: 'dd', resolve: () => pad(EXAMPLE_VALUE.day, 2) },
    { token: 'd', resolve: () => String(EXAMPLE_VALUE.day) },
    { token: 'EEEE', resolve: () => WEEKDAY_NAMES[EXAMPLE_VALUE._dayOfWeek - 1] },
    { token: 'EEE', resolve: () => WEEKDAY_NAMES_SHORT[EXAMPLE_VALUE._dayOfWeek - 1] },
    { token: 'HH', resolve: () => pad(EXAMPLE_VALUE.hour, 2) },
    { token: 'H', resolve: () => String(EXAMPLE_VALUE.hour) },
    { token: 'hh', resolve: () => pad(hour24to12(EXAMPLE_VALUE.hour), 2) },
    { token: 'h', resolve: () => String(hour24to12(EXAMPLE_VALUE.hour)) },
    { token: 'mm', resolve: () => pad(EXAMPLE_VALUE.minute, 2) },
    { token: 'm', resolve: () => String(EXAMPLE_VALUE.minute) },
    { token: 'ss', resolve: () => pad(EXAMPLE_VALUE.second, 2) },
    { token: 's', resolve: () => String(EXAMPLE_VALUE.second) },
    { token: 'SSS', resolve: () => pad(EXAMPLE_VALUE.millisecond, 3) },
    { token: 'a', resolve: () => (EXAMPLE_VALUE.hour < 12 ? 'AM' : 'PM') },
    { token: 'Z', resolve: () => EXAMPLE_VALUE.offset },
    { token: 'z', resolve: () => EXAMPLE_VALUE.timeZone },
];

function formatPreview(pattern: string): string {
    let result = '';
    let index = 0;

    while (index < pattern.length) {
        if (pattern[index] === "'") {
            const closingQuote = pattern.indexOf("'", index + 1);
            if (closingQuote === -1) {
                result += pattern.slice(index + 1);
                break;
            }
            result += pattern.slice(index + 1, closingQuote);
            index = closingQuote + 1;
            continue;
        }

        let matched = false;
        for (const { token, resolve } of PREVIEW_TOKENS) {
            if (pattern.startsWith(token, index)) {
                result += resolve();
                index += token.length;
                matched = true;
                break;
            }
        }

        if (!matched) {
            result += pattern[index];
            index++;
        }
    }

    return result;
}

// ── Component ───────────────────────────────────────────────────────

interface DateTimeFormatEditorProps {
    nodeState: Record<string, any>;
    setNodeState: (key: string, value: any) => void;
}

const DEFAULT_PATTERN = 'yyyy-MM-dd HH:mm:ss';

export const DateTimeFormatEditor: React.FC<DateTimeFormatEditorProps> = ({ nodeState, setNodeState }) => {
    const pattern = (nodeState.formatPattern as string) || DEFAULT_PATTERN;
    const preview = formatPreview(pattern);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setNodeState('formatPattern', event.target.value);
    };

    return (
        <div className="flex flex-col gap-1.5 px-2 py-1.5">
            <input
                type="text"
                value={pattern}
                onChange={handleChange}
                placeholder={DEFAULT_PATTERN}
                className="w-52 rounded bg-white/80 text-black text-xs px-1.5 py-1 outline-none focus:ring-1 focus:ring-white"
                spellCheck={false}
            />
            <div className="text-[10px] text-muted-foreground truncate" title={preview}>
                {preview}
            </div>
        </div>
    );
};
